/**
 * Cài đặt → Thương hiệu: tên, khẩu hiệu, màu, tiêu đề trang, favicon, thông tin hỗ trợ,
 * lời mời kết bạn mặc định… của tổ chức. Hiện trên trang đăng nhập ở tên miền riêng và
 * trong ứng dụng sau khi đăng nhập. Để trống = dùng mặc định.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ImageUp, Loader2, Trash2 } from 'lucide-react'
import { api, apiError } from '@/lib/api-client'
import { urlThuongHieu } from '@/lib/config'
import { apDungThuongHieu, boMauHeader } from '@/lib/thuong-hieu-to-chuc'
import { ChonMau, laGradient, mauDau } from '@/components/shared/chon-mau'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'

interface CaiDat {
  brandName: string | null; shortName: string | null; tagline: string | null; primaryColor: string | null; headerColor: string | null
  pageTitle: string | null; supportEmail: string | null; hotline: string | null; friendInvite: string | null
  nickSuffix: string | null; hidePlatformBrand: boolean; coFavicon: boolean; duocAnThuongHieuNenTang: boolean
  hieuLuc: { brandName: string; logoUrl: string; faviconUrl: string; primaryColor: string; tagline: string | null; platformName?: string }
}
type Truong = 'brandName' | 'shortName' | 'tagline' | 'primaryColor' | 'headerColor' | 'pageTitle' | 'supportEmail' | 'hotline' | 'friendInvite' | 'nickSuffix'

const KHOA = ['settings', 'branding']
const O: Array<{ k: Truong; nhan: string; goiY: string; toiDa: number }> = [
  { k: 'brandName', nhan: 'Tên thương hiệu', goiY: 'vd PharmaLink — để trống dùng tên tổ chức', toiDa: 60 },
  { k: 'shortName', nhan: 'Tên ngắn', goiY: 'vd PL — hiện ở chỗ hẹp', toiDa: 30 },
  { k: 'tagline', nhan: 'Khẩu hiệu', goiY: 'vd Nhà thuốc tận tâm', toiDa: 120 },
  { k: 'pageTitle', nhan: 'Tiêu đề tab trình duyệt', goiY: 'để trống = Tên thương hiệu — Khẩu hiệu', toiDa: 120 },
  { k: 'supportEmail', nhan: 'Email hỗ trợ', goiY: 'hotro@nhathuoc.vn', toiDa: 120 },
  { k: 'hotline', nhan: 'Hotline', goiY: '1900 xxxx', toiDa: 30 },
  { k: 'friendInvite', nhan: 'Lời mời kết bạn Zalo mặc định', goiY: 'vd Em là nhân viên PharmaLink, kết bạn để hỗ trợ mình nhé', toiDa: 150 },
  { k: 'nickSuffix', nhan: 'Đuôi tên nick Zalo (AI bỏ khi xưng hô)', goiY: 'vd PharmaLink — nick "Lan PharmaLink" → AI xưng "Lan"', toiDa: 80 },
]

function docAnh(f: File): Promise<string> {
  return new Promise((ok, loi) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => loi(r.error); r.readAsDataURL(f) })
}

export function ThuongHieuTab() {
  const qc = useQueryClient()
  const { data } = useQuery<CaiDat>({ queryKey: KHOA, queryFn: async () => (await api.get<CaiDat>('/settings/branding')).data })
  const [form, setForm] = useState<Record<Truong, string>>({} as Record<Truong, string>)
  const [an, setAn] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!data) return
    const f = Object.fromEntries(O.map((o) => [o.k, data[o.k] ?? ''])) as Record<Truong, string>
    setForm({ ...f, primaryColor: data.primaryColor ?? '', headerColor: data.headerColor ?? '' })
    setAn(!!data.hidePlatformBrand)
  }, [data])

  const capNhat = (d: CaiDat) => {
    qc.setQueryData(KHOA, d)
    apDungThuongHieu({ brandName: d.brandName, tagline: d.tagline, primaryColor: d.primaryColor, headerColor: d.headerColor, pageTitle: d.pageTitle, faviconUrl: d.coFavicon ? d.hieuLuc.faviconUrl : null })
  }
  const luu = useMutation({
    mutationFn: async () => (await api.put<CaiDat>('/settings/branding', { ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null])), hidePlatformBrand: an })).data,
    onSuccess: (d) => { capNhat(d); toast.success('Đã lưu thương hiệu') },
    onError: (e) => toast.error(apiError(e)),
  })
  const favicon = useMutation({
    mutationFn: async (dataUrl: string | null) => (dataUrl
      ? await api.put<CaiDat>('/settings/branding/favicon', { dataUrl })
      : await api.delete<CaiDat>('/settings/branding/favicon')).data,
    onSuccess: (d) => { capNhat(d); toast.success('Đã cập nhật favicon') },
    onError: (e) => toast.error(apiError(e)),
  })

  if (!data) return <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto animate-spin" /></div>
  // Màu chủ đạo có thể là gradient: nút dùng màu đầu, nền lớn dùng gradient.
  const mauGoc = form.primaryColor || data.hieuLuc.primaryColor
  const mau = mauDau(mauGoc) ?? '#2563eb'
  const nenDangNhap = laGradient(mauGoc) ? mauGoc : `linear-gradient(135deg, ${mau}, ${mau}b3)`
  const boHeader = boMauHeader(form.headerColor || '#172033')

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {O.map((o) => (
            <div key={o.k} className={o.k === 'friendInvite' || o.k === 'nickSuffix' ? 'sm:col-span-2' : ''}>
              <Label htmlFor={`th-${o.k}`} className="text-xs">{o.nhan}</Label>
              <Input id={`th-${o.k}`} value={form[o.k] ?? ''} maxLength={o.toiDa} placeholder={o.goiY} onChange={(e) => setForm((f) => ({ ...f, [o.k]: e.target.value }))} className="h-9" />
            </div>
          ))}
          <div>
            <Label htmlFor="th-mau" className="text-xs">Màu chủ đạo</Label>
            <ChonMau id="th-mau" value={form.primaryColor ?? ''} onChange={(v) => setForm((f) => ({ ...f, primaryColor: v }))} macDinh="Giữ màu hiện tại" />
            {laGradient(form.primaryColor) && <p className="mt-0.5 text-[10.5px] text-muted-foreground">Gradient dùng cho nền trang đăng nhập; nút & liên kết dùng màu đầu.</p>}
          </div>
        </div>

        <div className="space-y-2 rounded-lg border p-3">
          <div className="max-w-xs">
            <Label htmlFor="th-header" className="text-xs">Màu thanh menu trên cùng (header) — màu đơn hoặc gradient</Label>
            <ChonMau id="th-header" value={form.headerColor ?? ''} onChange={(v) => setForm((f) => ({ ...f, headerColor: v }))} macDinh="Màu mặc định" />
          </div>
          {boHeader && (
            <div className="flex h-10 items-center gap-3 rounded-md px-3 text-xs" style={{ background: boHeader.anh ?? `hsl(${boHeader.nen})`, color: `hsl(${boHeader.chu})`, borderBottom: `1px solid hsl(${boHeader.vien})` }}>
              <span className="rounded bg-white/95 px-1.5 py-0.5 font-bold text-[10px]" style={{ color: mau }}>{form.brandName || data.hieuLuc.brandName}</span>
              <span className="rounded px-2 py-1" style={{ background: `hsl(${boHeader.hover})` }}>Tổng quan</span>
              <span className="opacity-80">Hội thoại</span>
              <span className="opacity-80">Khách hàng</span>
              <span className="ml-auto opacity-80">Xem trước header</span>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">Chữ và icon trên header tự chuyển sáng/tối cho dễ đọc.</p>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">Ẩn thương hiệu nền tảng</p>
            <p className="text-xs text-muted-foreground">{data.duocAnThuongHieuNenTang ? 'Không hiện "Vận hành bởi" của nền tảng ở trang đăng nhập và chân trang.' : 'Chỉ có ở gói Doanh nghiệp.'}</p>
          </div>
          <Switch checked={an} disabled={!data.duocAnThuongHieuNenTang} onCheckedChange={setAn} />
        </div>

        <div className="flex items-center gap-3 rounded-lg border p-3">
          <img src={urlThuongHieu(data.hieuLuc.faviconUrl)} alt="" className="h-8 w-8 rounded border object-contain" />
          <div className="flex-1">
            <p className="text-sm font-medium">Favicon (biểu tượng tab trình duyệt)</p>
            <p className="text-xs text-muted-foreground">PNG / SVG / ICO vuông, ≤ 1 MB. Logo sửa ở mục Thông tin công ty.</p>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,image/webp,image/jpeg" className="hidden"
            onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) favicon.mutate(await docAnh(f)) }} />
          <Button size="sm" variant="outline" disabled={favicon.isPending} onClick={() => fileRef.current?.click()}><ImageUp /> Tải lên</Button>
          {data.coFavicon && <Button size="sm" variant="ghost" disabled={favicon.isPending} onClick={() => favicon.mutate(null)}><Trash2 /></Button>}
        </div>

        <div className="flex justify-end">
          <Button onClick={() => luu.mutate()} disabled={luu.isPending}>{luu.isPending && <Loader2 className="animate-spin" />} Lưu thương hiệu</Button>
        </div>
      </div>

      {/* Xem trước trang đăng nhập */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Xem trước trang đăng nhập</p>
        <div className="rounded-xl p-4" style={{ background: nenDangNhap }}>
          <div className="rounded-lg bg-background p-5 text-center shadow">
            {data.hieuLuc.logoUrl
              ? <img src={urlThuongHieu(data.hieuLuc.logoUrl)} alt="" className="mx-auto h-10 max-w-[160px] object-contain" />
              : <p className="text-lg font-bold" style={{ color: mau }}>{form.brandName || data.hieuLuc.brandName}</p>}
            <p className="mt-2 text-xs text-muted-foreground">{form.tagline || 'Quản lý khách hàng thông minh'}</p>
            <div className="mt-4 space-y-2">
              <div className="h-8 rounded border bg-muted/40" />
              <div className="h-8 rounded border bg-muted/40" />
              <div className="h-8 rounded text-xs font-medium leading-8 text-white" style={{ background: mau }}>Đăng nhập</div>
            </div>
            {!an && <p className="mt-3 text-[10px] text-muted-foreground">Vận hành bởi {data.hieuLuc.platformName ?? 'nền tảng'}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
